import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  AlertCircle,
  ArrowUp,
  Box,
  Brain,
  Check,
  CircleCheck,
  Eye,
  Lightbulb,
  Lock,
  Maximize2,
  Minimize2,
  MousePointerClick,
  RotateCcw,
  TextCursorInput,
  Trophy,
  X,
  XCircle,
} from "lucide-react";
import { hintFor, tracePrefixLength, validateAnswer } from "./answerValidator";
import { cardById, isCardAvailable, regionById } from "./cards";
import {
  emptyRecallStore,
  loadRecallStore,
  recordAnswer,
  recordStageComplete,
  saveRecallStore,
  summarizeCard,
} from "./progressService";
import { useRecallController } from "./recallController";
import { applyView, explorerUrl, prepareExplorer, readBuild, setInspect, setSystems } from "./explorerBridge";
import { gradientVars, ProgressRing, useRecallT } from "./ui";

const STAGES = ["trace", "recall", "master"];
const STAGE_META = {
  trace: ["stageTrace", "Trace", TextCursorInput],
  recall: ["stageRecall", "Recall", Lightbulb],
  master: ["stageMaster", "Master", Trophy],
};

export default function RecallPractice({ cardId, initialStage, dark, userId, onExit }) {
  const card = cardById(cardId);
  const { t } = useRecallT();
  if (!card || !isCardAvailable(card)) {
    return (
      <div className="ar-done" style={{ margin: 0 }}>
        <b>{card?.title || t("comingSoon", "Coming soon")}</b>
        <p>{card?.unavailableReason || t("notInModel", "Not yet supported by the 3D model")}</p>
        <button type="button" className="ar-ghost-btn" onClick={onExit}>
          {t("back", "Back")}
        </button>
      </div>
    );
  }
  return (
    <Practice
      card={card}
      initialStage={STAGES.includes(initialStage) ? initialStage : "trace"}
      dark={dark}
      userId={userId}
      onExit={onExit}
    />
  );
}

function Practice({ card, initialStage, dark, userId, onExit }) {
  const { t, lang } = useRecallT();
  const region = regionById(card.region);
  const gradient = region?.gradient || ["#FB7185", "#E11D48"];
  const chrome = dark ? { bg: "#140c0d", mask: "rgba(20,12,13,0.78)" } : { bg: "#f7f2f3", mask: "rgba(247,242,243,0.82)" };

  const frameRef = useRef(null);
  const shellRef = useRef(null);
  const inputRef = useRef(null);
  const storeRef = useRef(emptyRecallStore());
  const readyRef = useRef(false);
  const lastFocusKey = useRef(null);
  const timers = useRef([]);
  const [ready, setReady] = useState(false);
  const [buildProgress, setBuildProgress] = useState(0);
  const [recallStore, setRecallStore] = useState(emptyRecallStore());
  const [shake, setShake] = useState(0);
  const [flash, setFlash] = useState(0);
  const [fullscreen, setFullscreen] = useState(false);

  const ctl = useRecallController(card, initialStage);
  const { state, current, completedIds } = ctl;
  const traceLike = state.stage === "trace" || state.revealed;
  const total = state.queue.length;
  const done = state.completed.length;

  const later = useCallback((fn, ms) => {
    timers.current.push(window.setTimeout(fn, ms));
  }, []);
  useEffect(() => () => timers.current.forEach(window.clearTimeout), []);

  useEffect(() => {
    let live = true;
    loadRecallStore(userId).then((next) => {
      if (!live) return;
      storeRef.current = next;
      setRecallStore(next);
    });
    return () => {
      live = false;
    };
  }, [userId]);

  const persist = useCallback(
    (next) => {
      storeRef.current = next;
      setRecallStore(next);
      void saveRecallStore(userId, next);
    },
    [userId],
  );

  // Explorer URL must stay stable for the whole session (no reloads).
  // Freeze on card change so dark/lang flips don't remount the iframe.
  const srcRef = useRef({ cardId: null, url: "" });
  if (srcRef.current.cardId !== card.id) {
    srcRef.current = {
      cardId: card.id,
      url: explorerUrl({ systems: card.systems, dark, lang }),
    };
  }
  const src = srcRef.current.url;

  /* ───────────── mirror session onto the 3D viewer ───────────── */

  const syncViewer = useCallback(
    (forceFocus = false) => {
      const frame = frameRef.current;
      if (!readyRef.current || !frame) return;
      if (state.finished || !current) {
        lastFocusKey.current = null;
        setInspect(frame, false);
        applyView(frame, { activeIds: [], completedIds, progress: 1, focus: false });
        return;
      }
      const focus = forceFocus || lastFocusKey.current !== current.key;
      lastFocusKey.current = current.key;
      applyView(frame, {
        activeIds: current.ids,
        completedIds,
        progress: traceLike ? 0.15 + state.progress * 0.85 : 1,
        focus,
        angle: focus ? state.angle : 0,
      });
    },
    [completedIds, current, state.angle, state.finished, state.progress, traceLike],
  );
  const syncRef = useRef(syncViewer);
  syncRef.current = syncViewer;

  useEffect(() => {
    syncViewer();
  }, [syncViewer]);

  useEffect(() => {
    if (readyRef.current) setInspect(frameRef.current, state.revealed);
  }, [state.revealed]);

  // Poll the explorer's build status (same signal the mobile app listens to).
  useEffect(() => {
    const id = window.setInterval(() => {
      const frame = frameRef.current;
      prepareExplorer(frame);
      const build = readBuild(frame);
      if (!build) return;
      setBuildProgress(Math.max(0, Math.min(100, Math.round(Number(build.progress) || 0))));
      if ((build.status === "built" || build.progress >= 100) && !readyRef.current) {
        readyRef.current = true;
        setReady(true);
        setSystems(frame, card.systems);
        later(() => syncRef.current(true), 350);
        later(() => syncRef.current(true), 1100);
        later(() => inputRef.current?.focus(), 400);
      }
    }, 300);
    return () => window.clearInterval(id);
  }, [card.systems, later]);

  useEffect(() => {
    const onChange = () => setFullscreen(document.fullscreenElement === shellRef.current);
    document.addEventListener("fullscreenchange", onChange);
    return () => document.removeEventListener("fullscreenchange", onChange);
  }, []);

  const toggleFullscreen = () => {
    if (document.fullscreenElement) document.exitFullscreen?.();
    else shellRef.current?.requestFullscreen?.();
  };

  /* ───────────── answering ───────────── */

  const complete = useCallback(
    (typo) => {
      if (!current) return;
      const independent = state.wrongAttempts === 0 && state.hintLevel === 0 && !state.revealed;
      let next = recordAnswer(storeRef.current, {
        cardId: card.id,
        key: current.key,
        stage: state.stage,
        independent: state.stage !== "trace" && independent,
        correct: true,
        hintsUsed: state.hintLevel,
        revealed: state.revealed,
        wrongAttempts: state.wrongAttempts,
      });
      if (state.index + 1 >= state.queue.length) next = recordStageComplete(next, card.id, state.stage);
      persist(next);
      setFlash((n) => n + 1);
      later(() => setFlash(0), 450);
      ctl.correct(typo);
    },
    [card.id, ctl, current, later, persist, state],
  );

  const bump = useCallback(() => {
    setShake((n) => n + 1);
    later(() => setShake(0), 260);
  }, [later]);

  const onChange = (event) => {
    const raw = event.target.value;
    if (!current || state.finished) return;
    if (!traceLike) {
      ctl.setTyped(raw, 1);
      return;
    }
    const canonical = current.name;
    if (raw.length <= state.typed.length) {
      const kept = canonical.slice(0, tracePrefixLength(raw, canonical));
      ctl.setTyped(kept, kept.length / canonical.length);
      return;
    }
    // Auto-insert separators (spaces, hyphens) the learner skipped.
    let candidate = raw;
    let ok = tracePrefixLength(candidate, canonical);
    while (
      ok < candidate.length &&
      ok < canonical.length &&
      /[^a-z0-9]/i.test(canonical[ok]) &&
      candidate[ok] !== canonical[ok]
    ) {
      candidate = candidate.slice(0, ok) + canonical[ok] + candidate.slice(ok);
      ok = tracePrefixLength(candidate, canonical);
    }
    const accepted = canonical.slice(0, ok);
    if (ok < candidate.length) {
      ctl.setTyped(accepted, ok / canonical.length);
      ctl.traceSlip();
      bump();
      return;
    }
    if (ok >= canonical.length) {
      complete(false);
      return;
    }
    ctl.setTyped(accepted, ok / canonical.length);
  };

  const submit = () => {
    if (!current || traceLike || state.finished) return;
    if (!state.typed.trim()) {
      bump();
      return;
    }
    const result = validateAnswer(state.typed, current);
    if (result.outcome === "correct") {
      complete(result.typo);
      return;
    }
    ctl.wrong(result.outcome === "close");
    bump();
  };

  const startStage = (stage) => {
    lastFocusKey.current = null;
    ctl.start(stage, storeRef.current);
    later(() => inputRef.current?.focus(), 250);
  };

  const summary = useMemo(() => summarizeCard(card, recallStore), [card, recallStore]);
  const stageLabel = t(STAGE_META[state.stage][0], STAGE_META[state.stage][1]);

  /* ───────────── feedback line ───────────── */

  const fb = state.feedback;
  let FbIcon = STAGE_META[state.stage][2];
  let fbColor = "var(--ar-muted)";
  let fbText =
    state.stage === "trace"
      ? t("promptTrace", "Type the glowing structure — follow the ghost letters")
      : state.stage === "recall"
        ? t("promptRecall", "What is the glowing structure?")
        : t("promptMaster", "Name it from memory — new angle, no hints shown");
  if (state.revealed) {
    FbIcon = Eye;
    fbColor = "var(--ar-warn)";
    fbText = t("promptRevealed", "Revealed — type it out to lock it in");
  } else if (fb.kind === "correct") {
    FbIcon = CircleCheck;
    fbColor = "var(--ar-success)";
    fbText = fb.typo
      ? t("feedbackTypo", `Correct — spelled “${fb.name}”`, { name: fb.name })
      : t("feedbackCorrect", `${fb.name} ✓`, { name: fb.name });
  } else if (fb.kind === "close") {
    FbIcon = AlertCircle;
    fbColor = "var(--ar-warn)";
    fbText = t("feedbackClose", "So close — check the spelling");
  } else if (fb.kind === "wrong") {
    FbIcon = XCircle;
    fbColor = "var(--ar-danger)";
    fbText = fb.tries >= 2 ? t("feedbackWrongHint", "Not quite — try a hint?") : t("feedbackWrong", "Not quite — try again");
  } else if (fb.kind === "traceSlip") {
    FbIcon = MousePointerClick;
    fbColor = "var(--ar-warn)";
    fbText = t("feedbackSlip", "Oops — follow the ghost letters");
  }

  const firstTry = state.results.filter((r) => r.firstTry).length;
  const missed = state.results.filter((r) => !r.firstTry);
  const accuracy = state.results.length ? Math.round((firstTry / state.results.length) * 100) : 0;
  const hintText = !traceLike && current && state.hintLevel > 0 ? hintFor(current.name, state.hintLevel) : "";

  return (
    <div
      ref={shellRef}
      className="ar-practice ar-fade"
      style={{ ...gradientVars(gradient), "--ar-chrome": chrome.bg, "--ar-mask": chrome.mask }}
    >
      <div className="ar-viewer">
        <iframe ref={frameRef} src={src} title={card.title} onLoad={() => prepareExplorer(frameRef.current)} />

        <div className="ar-pheader">
          <div className="ar-pheader-row">
            <button type="button" className="ar-btn-round" onClick={onExit} aria-label={t("close", "Close")}>
              <X />
            </button>
            <div className="ar-pheader-text">
              <b>{t(`card_${card.id}`, card.title)}</b>
              <small>
                {stageLabel} · {Math.min(done + (state.finished ? 0 : 1), total)}/{total}
              </small>
            </div>
            <button
              type="button"
              className="ar-btn-round"
              onClick={toggleFullscreen}
              aria-label={t("fullscreen", "Fullscreen")}
            >
              {fullscreen ? <Minimize2 /> : <Maximize2 />}
            </button>
            <ProgressRing size={42} stroke={4} percent={total ? (done / total) * 100 : 0} colors={gradient}>
              <b style={{ fontSize: 12.5 }}>{done}</b>
            </ProgressRing>
          </div>
          <div className="ar-segments">
            {state.queue.map((entry, i) => (
              <i
                key={entry.key}
                className={
                  state.completed.includes(entry.key) ? "is-done" : i === state.index && !state.finished ? "is-active" : ""
                }
              />
            ))}
          </div>
          <div className="ar-stage-row">
            {STAGES.map((stage) => {
              const unlocked = summary.unlocked[stage] || stage === state.stage;
              const active = stage === state.stage;
              const Icon = unlocked ? STAGE_META[stage][2] : Lock;
              return (
                <button
                  key={stage}
                  type="button"
                  className={`ar-stage-chip ${unlocked ? "" : "is-locked"}`}
                  aria-pressed={active}
                  disabled={!unlocked || active}
                  onClick={() => startStage(stage)}
                >
                  <Icon />
                  {t(STAGE_META[stage][0], STAGE_META[stage][1])}
                </button>
              );
            })}
          </div>
        </div>

        {!ready ? (
          <div className="ar-mask">
            <ProgressRing size={76} stroke={6} percent={buildProgress} colors={gradient}>
              <b style={{ fontSize: 15 }}>{buildProgress}%</b>
            </ProgressRing>
            {t("preparing", "Preparing the 3D model…")}
          </div>
        ) : null}
      </div>

      {!state.finished && current ? (
        <div className={`ar-dock ${shake ? "is-shake" : ""} ${flash ? "is-flash" : ""}`}>
          <div className="ar-dock-inner">
            <div key={`${state.feedbackId}-${state.revealed}`} className="ar-feedback" style={{ "--fb": fbColor }}>
              <FbIcon />
              <span>{fbText}</span>
            </div>
            {hintText ? (
              <span className="ar-hint">
                <Lightbulb /> {hintText}
              </span>
            ) : null}
            <div className="ar-input-row">
              <label className={`ar-input-shell ${traceLike ? "is-trace" : ""}`}>
                {traceLike ? (
                  <span className="ar-ghost" aria-hidden>
                    <span className="t">{state.typed}</span>
                    <span className="c" />
                    <span className="g">{current.name.slice(state.typed.length)}</span>
                  </span>
                ) : null}
                <input
                  ref={inputRef}
                  value={state.typed}
                  onChange={onChange}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      e.preventDefault();
                      submit();
                    }
                  }}
                  autoComplete="off"
                  autoCorrect="off"
                  autoCapitalize="off"
                  spellCheck={false}
                  placeholder={traceLike ? "" : t("placeholder", "Type the name…")}
                  aria-label={t("inputLabel", "Structure name")}
                />
              </label>
              {traceLike ? (
                <span className="ar-counter">
                  {state.typed.length}/{current.name.length}
                </span>
              ) : (
                <button
                  type="button"
                  className="ar-submit"
                  disabled={!state.typed.trim()}
                  onClick={submit}
                  aria-label={t("check", "Check")}
                >
                  <ArrowUp />
                </button>
              )}
            </div>
            {!traceLike ? (
              <div className="ar-actions">
                <button type="button" className="ar-action" onClick={ctl.hint} disabled={state.hintLevel >= 2}>
                  <Lightbulb /> {t("hint", "Hint")}
                  {state.hintLevel ? ` ${state.hintLevel}/2` : ""}
                </button>
                <button type="button" className="ar-action" onClick={ctl.reveal}>
                  <Eye /> {t("reveal", "Reveal")}
                </button>
                <span className="ar-kbd">
                  <kbd>Enter</kbd> {t("toCheck", "to check")}
                </span>
              </div>
            ) : null}
          </div>
        </div>
      ) : null}

      {state.finished ? (
        <div className="ar-done">
          <div className="ar-dock-inner">
            <div className="ar-done-head">
              <ProgressRing size={78} stroke={7} percent={state.stage === "trace" ? 100 : accuracy} colors={gradient}>
                {state.stage === "master" ? <Trophy size={26} /> : state.stage === "recall" ? <Brain size={26} /> : <Box size={26} />}
              </ProgressRing>
              <div style={{ flex: 1, minWidth: 0 }}>
                <b>
                  {state.stage === "trace"
                    ? t("doneTraceTitle", "Model complete!")
                    : state.stage === "recall"
                      ? t("doneRecallTitle", "Recall complete")
                      : t("doneMasterTitle", "Master round done")}
                </b>
                <p>
                  {state.stage === "trace"
                    ? t(
                        "doneTraceBody",
                        `You traced all ${total} structures. Tracing isn't mastery yet — now test your memory.`,
                        { count: total },
                      )
                    : t("doneScore", `${firstTry}/${total} recalled first try · card mastery ${summary.mastery}%`, {
                        correct: firstTry,
                        total,
                        mastery: summary.mastery,
                      })}
                </p>
              </div>
            </div>
            {state.stage !== "trace" && missed.length ? (
              <div className="ar-missed">
                <small>{t("toRevise", "Scheduled for revision")}</small>
                <div>
                  {missed.slice(0, 10).map((r) => (
                    <span key={r.key}>{r.name}</span>
                  ))}
                </div>
              </div>
            ) : null}
            <button
              type="button"
              className="ar-primary"
              onClick={() => {
                if (state.stage === "trace") startStage("recall");
                else if (state.stage === "recall") startStage("master");
                else onExit();
              }}
            >
              {state.stage === "trace" ? <Lightbulb /> : state.stage === "recall" ? <Trophy /> : <Check />}
              {state.stage === "trace"
                ? t("testMemory", "Test My Memory")
                : state.stage === "recall"
                  ? t("goMaster", "Master Mode")
                  : t("done", "Done")}
            </button>
            <button type="button" className="ar-ghost-btn" onClick={() => startStage(state.stage)}>
              <RotateCcw /> {t("again", `${stageLabel} again`, { stage: stageLabel })}
            </button>
          </div>
        </div>
      ) : null}
    </div>
  );
}
